<template>
  <div class="ProspectingReport">
    <h1>
      <select name="days" class="days" @change="changeDays" v-model="dayCount">
        <option value="7">Last 7 Days</option>
        <option value="14">Last 14 Days</option>
        <option value="30">Last 30 Days</option>
        <option value="90">Last 90 Days</option>
        <option value="180">Last 180 Days</option>
        <option value="365">Last 365 Days</option>
      </select>
      <select
        name="days"
        class="days"
        @change="changeLangauge"
        v-model="languageFilter"
      >
        <option value="">All Languages</option>
        <option v-for="language in languages" :key="language" :value="language">
          {{ language.toUpperCase() }}
        </option>
      </select>
      Benchmarking Report
      <span v-if="reportMetaData"
        >({{
          moment
            .utc(reportMetaData.start)
            .local()
            .format("YYYY-MM-DD hh:mm a")
        }}
        -
        {{
          moment
            .utc(reportMetaData.end)
            .local()
            .format("YYYY-MM-DD hh:mm a")
        }})</span
      >
    </h1>
    <ul class="tabs">
      <li
        @click="changeTab('channels')"
        :class="{ 'is-active': currentTab === 'channels' }"
      >
        Channels
      </li>
      <li
        @click="changeTab('tiers')"
        :class="{ 'is-active': currentTab === 'tiers' }"
      >
        Tiers
      </li>
    </ul>
    <table v-show="currentTab === 'tiers'" cellpadding="0" cellspacing="0">
      <tr>
        <th colspan="2">Range</th>
        <th colspan="2">Growth</th>
        <th colspan="2">Channels</th>
      </tr>
      <tr>
        <th>Low</th>
        <th>High</th>
        <th>Mean</th>
        <th>Std. Dev</th>
        <th>#</th>
        <th>% Total</th>
      </tr>
      <tr v-for="range in reportFollowingData" :key="range.start">
        <td>
          {{ numberWithCommas(range.start) }}
        </td>
        <td>
          {{
            range.end === 100000000000
              ? numberWithCommas(range.start) + "+"
              : numberWithCommas(range.end)
          }}
        </td>
        <td>{{ range.meanGrowth.toFixed(2) }}%</td>
        <td>{{ range.growthStd.toFixed(2) }}%</td>
        <td>{{ range.channelCount }}</td>
        <td>{{ ((range.channelCount / rows.length) * 100).toFixed(2) }}%</td>
      </tr>
    </table>

    <table v-if="reportData" v-show="currentTab === 'channels'">
      <tr class="header">
        <th
          v-for="(column, index) in columns"
          :key="index"
          :class="{
            'is-active': sort.column === column.slug,
            'is-desc': sort.direction === 'desc',
            'u-tal': column.align === 'left'
          }"
          :colspan="column.colspan || 1"
          @click="sortBy(column.slug, column.defaultSort)"
        >
          <div class="container">
            <div class="text">{{ column.text }}</div>
            <span><chevron-up-icon size="1.25x"/></span>
          </div>
        </th>
      </tr>
      <tr
        v-for="(row, index) in rows"
        :key="index"
        :class="{ 'is-even': index % 2 === 0 }"
        class="titles"
      >
        <td class="logo">
          <img class="logo" :src="row.logo" alt="" />
        </td>
        <td class="u-tal channel">
          <a
            :href="`https://app.noscope.io/channels/twitch/${row.name}`"
            target="_blank"
            >{{ row.display_name }}</a
          >
        </td>
        <td>{{ row.language.toUpperCase() }}</td>
        <td>{{ Math.round(row.account_age / 30) }}</td>
        <td class="u-tal">
          <span v-if="!row.this_period.primary_game.isVariety">
            <a
              :href="
                `https://app.noscope.io/games/${row.this_period.primary_game.title}`
              "
              target="_blank"
            >
              {{ row.this_period.primary_game.title }}
            </a>
            ({{
              Math.round(
                ((row.this_period.primary_game.peek_count * 10) /
                  row.this_period.minutes_streamed) *
                  100
              )
            }}%)
          </span>
          <span v-else
            >Variety [<a
              :href="
                `https://app.noscope.io/games/${row.this_period.primary_game.mostPlayed}`
              "
              target="_blank"
              >{{ row.this_period.primary_game.mostPlayed }}</a
            >
            ({{
              Math.round(
                ((row.this_period.primary_game.peek_count * 10) /
                  row.this_period.minutes_streamed) *
                  100
              )
            }}%)]</span
          >
        </td>
        <td>
          {{ row.this_period.average_viewers_rank }}
        </td>
        <td>
          {{ numberWithCommas(row.this_period.average_viewers) }}
        </td>
        <td
          :class="{
            'is-good': row.this_period.average_viewers_growth > 0,
            'is-bad': row.this_period.average_viewers_growth < 0
          }"
        >
          {{ row.this_period.average_viewers_growth > 0 ? "+" : ""
          }}{{
            numberWithCommas(
              Math.round(row.this_period.average_viewers_growth)
            )
          }}%
        </td>
        <td :class="'viewershiptier-' + row.this_period.viewership_tier">
          <strong>{{ row.this_period.viewership_tier }}</strong>
        </td>
        <td>
          {{ numberWithCommas(Math.round(row.this_period.hours_streamed)) }}
          <VChangeIndicator
            v-if="row.last_period"
            :old-value="row.last_period.hours_streamed"
            :new-value="row.this_period.hours_streamed"
            :decimals="0"
          />
        </td>
        <td>
          {{ numberWithCommas(row.this_period.stream_count) }}
          <VChangeIndicator
            v-if="row.last_period"
            :old-value="row.last_period.stream_count"
            :new-value="row.this_period.stream_count"
            :decimals="0"
          />
        </td>
        <td>
          {{ numberWithCommas(Math.round(row.this_period.hours_watched)) }}
          <VChangeIndicator
            v-if="row.last_period"
            :old-value="row.last_period.minutes_watched"
            :new-value="row.this_period.minutes_watched"
            :decimals="0"
            percentage
          />
        </td>
        <td>{{ numberWithCommas(row.this_period.followers) }}</td>
        <td
          :class="{
            'is-good': row.this_period.followers_gained > 0,
            'is-bad': row.this_period.followers_gained < 0
          }"
        >
          {{ row.this_period.followers_gained > 0 ? "+" : ""
          }}{{ numberWithCommas(row.this_period.followers_gained) || "0" }}
        </td>
        <td
          :class="{
            'is-good': row.this_period.growth > 0,
            'is-bad': row.this_period.growth < 0
          }"
        >
          {{ row.this_period.growth > 0 ? "+" : ""
          }}{{ numberWithCommas(row.this_period.growth.toFixed(0)) }}%
        </td>
        <td
          :class="{
            'is-good': row.zScore > 0,
            'is-bad': row.zScore < 0
          }"
        >
          {{ row.zScore > 0 ? "+" : ""
          }}{{ row.zScore ? numberWithCommas(row.zScore.toFixed(1)) : "" }}
        </td>
        <td
          :class="{
            'is-good': row.this_period.followers_per_hour > 0,
            'is-bad': row.this_period.followers_per_hour < 0
          }"
        >
          {{ row.this_period.followers_per_hour > 0 ? "+" : ""
          }}{{
            numberWithCommas(row.this_period.followers_per_hour.toFixed(0))
          }}
        </td>
        <td
          :class="{
            'is-good': row.this_period.per_hour_growth > 0,
            'is-bad': row.this_period.per_hour_growth < 0
          }"
        >
          {{ row.this_period.per_hour_growth > 0 ? "+" : ""
          }}{{ row.this_period.per_hour_growth.toFixed(2) }}%
        </td>
      </tr>
    </table>

    <VCandyCaneLoader style="margin: 50px auto; max-width: 200px" v-else />
  </div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import _ from "lodash";
import router from "@/router";
import VCandyCaneLoader from "@/components/loading/VCandyCaneLoader";
import { ChevronUpIcon } from "@zhuowenli/vue-feather-icons";
import moment from "moment";
import VChangeIndicator from "@/components/ui/VChangeIndicator";
import { std } from "mathjs";

import LoadedRoster from "@/data/LoadedRoster";
import FollowerTiers from "@/data/FollowerTiers";
import ViewershipTiers from "@/data/ViewershipTiers";
import columns from "./columns";

function determineViewershipRank(viewership) {
  let output = null;
  _.forEach(ViewershipTiers, (rank, index) => {
    if (rank[0] <= viewership && rank[1] >= viewership) {
      output = index + 1;
    }
  });

  return output;
}

export default {
  name: "ProspectingReport",
  components: { ChevronUpIcon, VCandyCaneLoader, VChangeIndicator },
  setup() {
    const route = useRoute();
    const reportData = ref(null);
    const reportMetaData = ref(null);
    const reportFollowingData = ref(null);
    const dayCount = ref(route.params.days);
    const languageFilter = ref("");
    const sort = reactive({
      column: "this_period.average_viewers_rank",
      direction: "asc"
    });

    const rows = computed(() => {
      if (
        sort.column === "name" ||
        sort.column === "this_period.primary_game.title"
      ) {
        return reportData.value;
      }

      let output = reportData.value;

      if (languageFilter.value) {
        output = _.filter(output, (row) => {
          return row.language === languageFilter.value;
        });
      }

      output = _.filter(output, (row) => {
        return row.this_period.minutes_streamed / 60 / 20 > dayCount.value / 30;
      });

      return _.take(output, 10000);
    });

    const currentTab = ref("channels");
    const languages = ref([]);

    function generateFollowersMap(channels) {
      let map = {};
      _.forEach(FollowerTiers, (range) => {
        map[range[0] + "-" + range[1]] = {
          start: range[0],
          end: range[1],
          growthTotal: 0,
          meanGrowth: 0,
          channelCount: 0,
          channels: [],
          growths: [],
          growthStd: 0
        };
      });

      _.forEach(channels, (channel) => {
        _.forEach(map, (range, slug) => {
          if (
            channel.this_period.followers > range.start &&
            channel.this_period.followers < range.end
          ) {
            range.growthTotal = range.growthTotal + channel.this_period.growth;
            range.channelCount++;
            range.meanGrowth = range.growthTotal / range.channelCount;
            range.channels.push(channel);
            range.growths.push(channel.this_period.growth);
            range.growthStd = std(range.growths);
          }

          if (
            channel.last_period &&
            channel.last_period.followers > range.start &&
            channel.last_period.followers < range.end
          ) {
            channel.last_period.range = slug;
          }
        });
      });

      // Fill in Z Score
      _.forEach(channels, (channel) => {
        if (!channel.last_period) {
          channel.zScore = 0;
        } else {
          const oldRange = map[channel.last_period.range];
          channel.zScore =
            (channel.this_period.growth - oldRange.meanGrowth) /
            oldRange.growthStd;
        }
      });

      reportFollowingData.value = map;

      // _.forEach(map, (range) => {
      //   _.forEach(range.channels, function() {
      //     growthTotal++;
      //   });
      // });
    }

    function getData() {
      axios
        .get("/v1/reports/prospecting/" + dayCount.value)
        .then((response) => {
          reportMetaData.value = response.data.meta;
          reportData.value = _.map(response.data.data, (item) => {
            let thisPeriod = item.this_period;
            let lastPeriod = item.last_period;

            if (!thisPeriod.followers) {
              thisPeriod.followers = 0;
            }

            thisPeriod.hours_watched = thisPeriod.minutes_watched / 60;
            thisPeriod.hours_streamed = thisPeriod.minutes_streamed / 60;

            if (
              thisPeriod.primary_game.peek_count * 10 <
              thisPeriod.minutes_streamed * 0.33
            ) {
              thisPeriod.primary_game.mostPlayed =
                thisPeriod.primary_game.title;
              thisPeriod.primary_game.title = "Variety";
              thisPeriod.primary_game.isVariety = true;
            }

            item.isLoaded = LoadedRoster.includes(item.name);

            if (item.last_period) {
              thisPeriod.followers_gained =
                thisPeriod.followers - lastPeriod.followers;

              thisPeriod.growth =
                (thisPeriod.followers_gained / lastPeriod.followers) * 100;

              thisPeriod.followers_per_hour =
                thisPeriod.followers_gained /
                (thisPeriod.minutes_streamed / 60);

              thisPeriod.per_hour_growth =
                (thisPeriod.followers_per_hour / lastPeriod.followers) * 100;

              lastPeriod.hours_streamed = lastPeriod.minutes_streamed / 60;
              thisPeriod.average_viewers_growth = Math.abs(
                ((thisPeriod.average_viewers - lastPeriod.average_viewers) /
                  lastPeriod.average_viewers) *
                  100
              );
            } else {
              thisPeriod.followers_gained = 0;
              thisPeriod.growth = 0;
              thisPeriod.followers_per_hour = 0;
              thisPeriod.per_hour_growth = 0;
              thisPeriod.average_viewers_growth = 0;
            }

            thisPeriod.viewership_tier = determineViewershipRank(
              thisPeriod.average_viewers
            );

            return item;
          });

          sortBy(sort.column, false);
          generateFollowersMap(reportData.value);
          languages.value = _.uniq(
            _.map(reportData.value, (item) => {
              return item.language;
            })
          ).sort();
        });
    }

    function numberWithCommas(x) {
      if (!x) {
        return x;
      }
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    function changeDays() {
      router.replace({
        name: "ProspectingReport",
        params: { days: dayCount.value }
      });
      reportData.value = null;
      getData();
    }

    function sortBy(column, defaultDirection) {
      if (defaultDirection !== false) {
        if (sort.column === column) {
          sort.direction = sort.direction === "asc" ? "desc" : "asc";
        } else {
          sort.direction = defaultDirection;
        }
      }
      sort.column = column;

      setTimeout(function() {
        reportData.value = _.orderBy(reportData.value, column, sort.direction);
      }, 200);
    }

    function changeLanguage() {
      getData();
      //generateFollowersMap(rows.value);
    }

    function changeTab(tab) {
      currentTab.value = tab;
    }

    getData();

    return {
      changeDays,
      changeTab,
      changeLanguage,
      currentTab,
      columns,
      dayCount,
      languages,
      languageFilter,
      moment,
      numberWithCommas,
      reportData,
      reportFollowingData,
      reportMetaData,
      rows,
      sort,
      sortBy
    };
  }
};
</script>

<style lang="scss" scoped>
.ProspectingReport {
  .tabs {
    margin: 50px 50px -51px 50px;
    li {
      display: inline-block;
      color: #888;
      padding: 12px 24px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.2s, box-shadow 0.2s, color 0.2s,
        border-color 0.2s;
      border: 1px solid transparent;
      border-bottom: 0;
      &.is-active {
        background-color: #fff;
        border: 1px solid #ececec;
        border-bottom: none;
        color: #000;
        box-shadow: 0 -3px 10px 3px rgba(0, 0, 0, 0.025);
      }
      &:not(.is-active):hover {
        color: #333;
      }
    }
  }
  .logo {
    height: 42px;
    width: 42px;
    display: inline-block;
    vertical-align: middle;
  }

  .days {
    float: right;
    background-color: #fff;
    padding: 6px 10px;
    border: 1px solid #aaa;
  }

  .u-tal {
    text-align: left !important;
  }

  h1 {
    padding: 50px;
    padding-bottom: 0;
    font-size: 24px;
    font-weight: 600;
  }

  table {
    margin: 50px;
    background-color: #fff;
    border-top: 1px solid #ececec;
    border-left: 1px solid #ececec;
    box-shadow: 2px 2px 0 0 rgba(0, 0, 0, 0.2);
    a {
      color: inherit;
      transition: color 0.2s;
      &:hover {
        color: #f55934;
      }
    }
    .channel {
      font-weight: 600;
    }
    th {
      position: sticky;
      background-color: #fff;
      //box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.075);
      top: 1px;
      font-weight: 600;
      font-size: 11px;
      text-align: left;
      vertical-align: middle;
      padding: 16px 10px;
      text-transform: uppercase;
      text-align: left;
      border-right: 1px solid rgba(0, 0, 0, 0);
      cursor: pointer;
      box-shadow: inset 0 0 0 0 #000;
      transition: box-shadow 0.2s, color 0.15s;
      box-shadow: 0 0 0 0.5px #efefef !important;
      &:active {
        .container {
          transform: translateY(1px);
        }
      }
      &:hover {
        color: #666;
      }
      .container {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .text {
        flex-grow: 1;
        width: 100%;
      }
      span {
        opacity: 0;
        vertical-align: middle;
        width: 0px;
        width: 16px;
        transition: opacity 0.15s, transform 0.2s;
      }
      &.is-active {
        box-shadow: inset 0 -3px 0 0 #000 !important;
        span {
          opacity: 1;
          display: block;
          flex-grow: 1;
        }
      }
      &.is-desc {
        span {
          transform: rotate(180deg);
        }
      }
    }
    td {
      padding: 8px 12px;
      font-size: 14px;
      border-top: 1px solid rgba(0, 0, 0, 0.04);
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      text-align: center;
      transition: background-color 0.2s;
      border-right: 1px solid rgba(0, 0, 0, 0.04);
      white-space: nowrap;
      strong {
        font-weight: 600 !important;
      }
      &.viewershiptier-1 {
        color: rgba(0, 0, 0, 0.485);
        background-color: rgba(green, 0.6) !important;
      }
      &.viewershiptier-2 {
        color: rgba(0, 0, 0, 0.45);
        background-color: rgba(green, 0.5) !important;
      }
      &.viewershiptier-3 {
        color: rgba(0, 0, 0, 0.4);
        background-color: rgba(green, 0.4) !important;
      }
      &.viewershiptier-4 {
        color: rgba(0, 0, 0, 0.35);
        background-color: rgba(green, 0.3) !important;
      }
      &.viewershiptier-5 {
        color: rgba(0, 0, 0, 0.3);
        background-color: rgba(green, 0.2) !important;
      }
      &.viewershiptier-6 {
        color: rgba(0, 0, 0, 0.25);
        background-color: rgba(green, 0.1) !important;
      }
      &.viewershiptier-7 {
        color: rgba(0, 0, 0, 0.2);
      }
      &.logo {
        border-right: 1px solid #ccc;
        background-color: #777;
        width: 42px;
        padding: 0;
      }
      &.is-good {
        color: green;
        font-weight: 600;
      }
      &.is-bad {
        color: red;
        font-weight: 600;
      }
    }
    .is-even {
      td:not(.logo) {
        background-color: #fbfbfb;
      }
    }
    tr {
      &:hover {
        td:not(.logo) {
          background-color: rgba(#000, 0.04);
          border-color: rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
}
</style>
